import { useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tags, Plus, Trash2, Save, X, FolderTree } from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/cn';

export interface Taxonomy {
  id: number;
  key: string;
  label_singular: string;
  label_plural: string;
  hierarchical: boolean;
  icon?: string | null;
  terms_count?: number;
}

export interface Term {
  id: string;
  taxonomy_key: string;
  parent_id: string | null;
  slug: string;
  name: string;
  description?: string | null;
  color?: string | null;
  order: number;
  posts_count?: number;
}

export const Route = createFileRoute('/_app/taxonomies')({
  component: TaxonomiesPage,
});

function TaxonomiesPage() {
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const { data: taxonomies, isLoading } = useQuery({
    queryKey: ['taxonomies'],
    queryFn: () => unwrap<Taxonomy[]>(api.get('/taxonomies')),
  });

  const active = useMemo(
    () => taxonomies?.find((t) => t.key === activeKey) ?? taxonomies?.[0] ?? null,
    [taxonomies, activeKey],
  );

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6 md:p-8">
      <header>
        <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
          <Tags className="size-7 text-[#AD026A]" /> Taxonomías
        </h1>
        <p className="text-sm text-neutral-500">
          Organiza tus contenidos con categorías (jerárquicas) y etiquetas. Cada término puede enlazarse a los artículos desde el editor.
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm text-neutral-500">Cargando taxonomías…</p>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr]">
          <aside className="self-start rounded-2xl border border-neutral-200 bg-white p-3">
            <ul className="space-y-0.5">
              {taxonomies?.map((t) => {
                const isActive = active?.key === t.key;
                return (
                  <li key={t.key}>
                    <button
                      type="button"
                      onClick={() => setActiveKey(t.key)}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                        isActive ? 'text-white' : 'text-neutral-600 hover:bg-neutral-100',
                      )}
                      style={isActive ? { background: 'linear-gradient(90deg, #66023C 0%, #AD026A 100%)' } : undefined}
                    >
                      <span className="flex items-center gap-2">
                        {t.hierarchical ? <FolderTree className="size-4" /> : <Tags className="size-4" />}
                        {t.label_plural}
                      </span>
                      <Badge variant={isActive ? 'default' : 'secondary'} className={cn('text-[10px]', isActive && 'bg-white/15 text-white')}>
                        {t.terms_count ?? 0}
                      </Badge>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {active ? <TaxonomyPanel taxonomy={active} /> : null}
        </div>
      )}
    </div>
  );
}

function TaxonomyPanel({ taxonomy }: { taxonomy: Taxonomy }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Term | null>(null);

  const { data: terms } = useQuery({
    queryKey: ['terms', taxonomy.key],
    queryFn: () => unwrap<Term[]>(api.get(`/taxonomies/${taxonomy.key}/terms`)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/terms/${id}`),
    onSuccess: () => {
      toast.success('Término eliminado');
      void qc.invalidateQueries({ queryKey: ['terms', taxonomy.key] });
      void qc.invalidateQueries({ queryKey: ['taxonomies'] });
    },
    onError: () => toast.error('No se pudo eliminar'),
  });

  const ordered = useMemo(() => buildTree(terms ?? [], taxonomy.hierarchical), [terms, taxonomy.hierarchical]);

  return (
    <div className="space-y-6">
      <TermForm
        taxonomy={taxonomy}
        terms={terms ?? []}
        editing={editing}
        onDone={() => setEditing(null)}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{taxonomy.label_plural}</CardTitle>
          <CardDescription>
            {(terms?.length ?? 0)} {taxonomy.hierarchical ? 'categorías' : 'etiquetas'}. Pulsa una para editarla.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(terms?.length ?? 0) === 0 ? (
            <p className="py-6 text-center text-sm text-neutral-400">Aún no hay términos. Crea el primero arriba.</p>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {ordered.map(({ term, depth }) => (
                <li key={term.id} className="flex items-center justify-between gap-3 py-2.5">
                  <button
                    type="button"
                    onClick={() => setEditing(term)}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    style={{ paddingLeft: `${depth * 18}px` }}
                  >
                    {term.color ? (
                      <span className="size-3 shrink-0 rounded-full" style={{ background: term.color }} />
                    ) : (
                      <span className="size-3 shrink-0 rounded-full border border-neutral-300" />
                    )}
                    <span className="truncate text-sm font-medium text-neutral-800 hover:underline">{term.name}</span>
                    <span className="truncate text-xs text-neutral-400">/{term.slug}</span>
                  </button>
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <Badge variant="secondary" className="text-[10px]">{term.posts_count ?? 0} posts</Badge>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`¿Eliminar «${term.name}»? Los posts no se borran, solo se desvincula.`)) {
                          remove.mutate(term.id);
                        }
                      }}
                      className="rounded p-1 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                      aria-label="Eliminar término"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TermForm({
  taxonomy, terms, editing, onDone,
}: {
  taxonomy: Taxonomy;
  terms: Term[];
  editing: Term | null;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [parentId, setParentId] = useState<string>('');
  const [color, setColor] = useState('');
  const [description, setDescription] = useState('');

  const isEditing = editing !== null;

  useEffect(() => {
    setName(editing?.name ?? '');
    setSlug(editing?.slug ?? '');
    setParentId(editing?.parent_id ?? '');
    setColor(editing?.color ?? '');
    setDescription(editing?.description ?? '');
  }, [editing]);

  const reset = () => {
    setName(''); setSlug(''); setParentId(''); setColor(''); setDescription('');
    onDone();
  };

  const mutation = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        slug: slug.trim() || undefined,
        parent_id: taxonomy.hierarchical && parentId ? parentId : null,
        color: color || null,
        description: description || null,
      };
      return editing
        ? api.put(`/terms/${editing.id}`, body)
        : api.post(`/taxonomies/${taxonomy.key}/terms`, body);
    },
    onSuccess: () => {
      toast.success(isEditing ? 'Término actualizado' : 'Término creado');
      void qc.invalidateQueries({ queryKey: ['terms', taxonomy.key] });
      void qc.invalidateQueries({ queryKey: ['taxonomies'] });
      reset();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg ?? 'No se pudo guardar');
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {isEditing ? `Editar «${editing?.name}»` : `Nueva ${taxonomy.label_singular.toLowerCase()}`}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="term_name">Nombre</Label>
            <Input id="term_name" value={name} onChange={(e) => setName(e.target.value)} placeholder={`p. ej. ${taxonomy.hierarchical ? 'Marketing' : 'SEO'}`} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="term_slug">Slug (URL)</Label>
            <Input id="term_slug" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="se genera del nombre" />
          </div>
          {taxonomy.hierarchical ? (
            <div className="space-y-1.5">
              <Label htmlFor="term_parent">Categoría padre</Label>
              <select
                id="term_parent"
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm"
              >
                <option value="">— Ninguna (nivel superior) —</option>
                {terms.filter((t) => t.id !== editing?.id).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="term_color">Color (opcional)</Label>
            <div className="flex items-center gap-2">
              <input
                id="term_color"
                type="color"
                value={color || '#AD026A'}
                onChange={(e) => setColor(e.target.value)}
                className="h-9 w-12 cursor-pointer rounded border border-neutral-200"
              />
              {color ? (
                <button type="button" onClick={() => setColor('')} className="text-xs text-neutral-400 hover:text-neutral-700">
                  Quitar
                </button>
              ) : null}
            </div>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="term_desc">Descripción (opcional)</Label>
          <Textarea id="term_desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => mutation.mutate()} disabled={!name.trim() || mutation.isPending}>
            {isEditing ? <Save className="size-4" /> : <Plus className="size-4" />}
            {mutation.isPending ? 'Guardando…' : isEditing ? 'Guardar cambios' : 'Añadir'}
          </Button>
          {isEditing ? (
            <Button variant="ghost" onClick={reset}>
              <X className="size-4" /> Cancelar
            </Button>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

/** Ordena los términos: árbol (padre→hijos) si es jerárquica, plano si no. */
function buildTree(terms: Term[], hierarchical: boolean): Array<{ term: Term; depth: number }> {
  if (!hierarchical) {
    return [...terms]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((term) => ({ term, depth: 0 }));
  }

  const byParent = new Map<string | null, Term[]>();
  for (const t of terms) {
    const key = t.parent_id ?? null;
    const arr = byParent.get(key) ?? [];
    arr.push(t);
    byParent.set(key, arr);
  }

  const out: Array<{ term: Term; depth: number }> = [];
  const walk = (parent: string | null, depth: number) => {
    const children = (byParent.get(parent) ?? []).sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
    for (const term of children) {
      out.push({ term, depth });
      walk(term.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}
